import _ from "lodash";
import { FuseFlatNavItemType } from "../FuseNavigation/types/FuseNavItemType";

const DEFAULT_MAX_SUGGESTIONS = 10;

type NavigationSearchIndexItem = Omit<FuseFlatNavItemType, "title" | "url"> & {
	title: string;
	url: string;
	searchContext: string;
	normalizedTitle: string;
	normalizedSubtitle: string;
	normalizedContext: string;
	normalizedSearchableText: string;
};

function normalizeText(value: string | undefined): string {
	return _.deburr(String(value ?? ""))
		.toLowerCase()
		.trim();
}

function tokenizeQuery(value: string): string[] {
	return normalizeText(value).split(/\s+/).filter(Boolean);
}

function getParentOrders(order: string): string[] {
	const segments = String(order ?? "")
		.split(".")
		.filter(Boolean);

	if (segments.length <= 1) {
		return [];
	}

	const parentOrders: string[] = [];

	for (let i = 1; i < segments.length; i += 1) {
		parentOrders.push(segments.slice(0, i).join("."));
	}

	return parentOrders;
}

function getBestMatchPosition(source: string, normalizedQuery: string, queryTokens: string[]): number {
	if (!source) {
		return Number.MAX_SAFE_INTEGER;
	}

	if (normalizedQuery) {
		const normalizedQueryPosition = source.indexOf(normalizedQuery);
		if (normalizedQueryPosition >= 0) {
			return normalizedQueryPosition;
		}
	}

	let bestPosition = Number.MAX_SAFE_INTEGER;
	for (const token of queryTokens) {
		const tokenPosition = source.indexOf(token);
		if (tokenPosition >= 0) {
			bestPosition = Math.min(bestPosition, tokenPosition);
		}
	}

	return bestPosition;
}

function isNavigableItem(item: FuseFlatNavItemType): item is FuseFlatNavItemType & { title: string; url: string } {
	return (
		item.type === "item" &&
		Boolean(item.title) &&
		Boolean(item.url) &&
		item.hasPermission !== false &&
		!item.disabled &&
		!item.hidden
	);
}

export type FuseSearchSuggestion = NavigationSearchIndexItem;

export function buildNavigationSearchIndex(navigation: FuseFlatNavItemType[]): FuseSearchSuggestion[] {
	const itemsByOrder = new Map<string, FuseFlatNavItemType>();

	for (const item of navigation) {
		itemsByOrder.set(item.order, item);
	}

	return navigation.filter(isNavigableItem).map((item) => {
		const parentTitles = getParentOrders(item.order)
			.map((parentOrder) => itemsByOrder.get(parentOrder)?.title)
			.filter((title): title is string => Boolean(title));

		const searchContext = parentTitles.join(" / ");
		const normalizedTitle = normalizeText(item.title);
		const normalizedSubtitle = normalizeText(item.subtitle);
		const normalizedContext = normalizeText(searchContext);
		const normalizedSearchableText = [normalizedTitle, normalizedSubtitle, normalizedContext]
			.filter(Boolean)
			.join(" ");

		return {
			...item,
			title: item.title,
			url: item.url,
			searchContext,
			normalizedTitle,
			normalizedSubtitle,
			normalizedContext,
			normalizedSearchableText,
		};
	});
}

export function getNavigationSuggestions(
	query: string,
	navigationIndex: FuseSearchSuggestion[],
	maxSuggestions = DEFAULT_MAX_SUGGESTIONS,
): FuseSearchSuggestion[] {
	const normalizedQuery = normalizeText(query);
	const queryTokens = tokenizeQuery(query);

	if (!queryTokens.length) {
		return [];
	}

	const scoredSuggestions = navigationIndex
		.map((item, position) => {
			const allTokensMatched = queryTokens.every((token) => item.normalizedSearchableText.includes(token));
			if (!allTokensMatched) {
				return null;
			}

			const title = item.normalizedTitle;
			const contextText = [item.normalizedContext, item.normalizedSubtitle].filter(Boolean).join(" ");
			const startsWithTitle = normalizedQuery.length > 0 && title.startsWith(normalizedQuery);
			const fullQueryInTitle = normalizedQuery.length > 0 && title.includes(normalizedQuery);
			const allTokensInTitle = queryTokens.every((token) => title.includes(token));
			const anyTokenInTitle = queryTokens.some((token) => title.includes(token));

			let category: 1 | 2 | 3 = 3;
			if (startsWithTitle) {
				category = 1;
			} else if (fullQueryInTitle || allTokensInTitle || anyTokenInTitle) {
				category = 2;
			}

			return {
				item,
				category,
				titleMatchPosition: getBestMatchPosition(title, normalizedQuery, queryTokens),
				contextMatchPosition: getBestMatchPosition(contextText, normalizedQuery, queryTokens),
				titleTokenHits: queryTokens.filter((token) => title.includes(token)).length,
				contextTokenHits: queryTokens.filter((token) => contextText.includes(token)).length,
				position,
			};
		})
		.filter((entry): entry is NonNullable<typeof entry> => entry !== null);

	scoredSuggestions.sort((left, right) => {
		if (left.category !== right.category) {
			return left.category - right.category;
		}

		if (left.titleMatchPosition !== right.titleMatchPosition) {
			return left.titleMatchPosition - right.titleMatchPosition;
		}

		if (left.contextMatchPosition !== right.contextMatchPosition) {
			return left.contextMatchPosition - right.contextMatchPosition;
		}

		if (left.titleTokenHits !== right.titleTokenHits) {
			return right.titleTokenHits - left.titleTokenHits;
		}

		if (left.contextTokenHits !== right.contextTokenHits) {
			return right.contextTokenHits - left.contextTokenHits;
		}

		if (left.item.title && right.item.title) {
			const titleComparison = left.item.title.localeCompare(right.item.title, "pt-BR");
			if (titleComparison !== 0) {
				return titleComparison;
			}
		}

		return left.position - right.position;
	});

	return scoredSuggestions.slice(0, maxSuggestions).map((entry) => entry.item);
}
